import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth, API } from "../App";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { 
  Sparkles, Upload, Users, Palette, Check, ArrowRight, Play, Star, 
  Crown, Gem, Shirt, Baby, Droplets, Clock, Zap, Shield, TrendingUp,
  Image, Video, Download, ChevronRight, Building, Phone, Mail, MapPin,
  CheckCircle2, Camera, Wand2, Package, Layers, PhoneCall, MessageCircle,
  ShoppingBag, Award, Target, Rocket, Eye, Heart
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/2zc3j2vv_jini%20studio%20logo%20.png";

// Before/After comparison images
const COMPARISON_IMAGES = [
  {
    before: "https://images.unsplash.com/photo-1558171813-4c088753af8f?w=800&q=80",
    after: "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800&q=80",
    title: "Fashion Photography",
    description: "From basic product shots to stunning model presentations"
  },
  {
    before: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=800&q=80",
    after: "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=800&q=80",
    title: "Cosmetics & Beauty",
    description: "Transform simple product photos into lifestyle imagery"
  },
  {
    before: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=800&q=80",
    after: "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=800&q=80",
    title: "Jewelry & Accessories",
    description: "Elevate jewelry with professional model shots"
  }
];

const DEMO_VIDEOS = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/5pril49p_WhatsApp%20Video%202026-01-30%20at%2017.46.06.mp4", title: "Ethnic Wear Showcase", category: "Ethnic" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/0bvqqy7k_WhatsApp%20Video%202026-01-29%20at%2018.07.50.mp4", title: "Fashion Collection", category: "Western" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/p0s82rkf_WhatsApp%20Video%202026-01-29%20at%2017.17.07%20%281%29.mp4", title: "Model Photoshoot", category: "Fashion" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/fn4t0ppn_WhatsApp%20Video%202026-01-29%20at%2017.17.07.mp4", title: "Product Showcase", category: "Lifestyle" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/d3ie3kf8_WhatsApp%20Video%202026-01-29%20at%2016.01.23.mp4", title: "Casual Collection", category: "Casual" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/x7ud3o2p_WhatsApp%20Video%202026-01-31%20at%2017.00.28.mp4", title: "Premium Lookbook", category: "Premium" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/2q4s2pq0_WhatsApp%20Video%202026-01-31%20at%2012.54.59.mp4", title: "Studio Shoot", category: "Studio" }
];

export default function LandingPage() {
  const { user } = useAuth();

  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  const handleBookDemo = () => {
    window.open("https://calendly.com/zarooriretail", "_blank");
  };

  const handleCall = () => {
    window.location.href = "tel:+917009008425";
  };

  const handleWhatsApp = () => {
    window.open("https://wa.me/917009008425?text=Hi, I'm interested in JiniStudio AI services", "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Jini Studio" className="h-12 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <a href="#what-is" className="text-gray-700 hover:text-purple-700 font-medium text-sm">What is JiniStudio</a>
            <a href="#services" className="text-gray-700 hover:text-purple-700 font-medium text-sm">Services</a>
            <a href="#why-choose" className="text-gray-700 hover:text-purple-700 font-medium text-sm">Why Choose Us</a>
            <a href="#pricing" className="text-gray-700 hover:text-purple-700 font-medium text-sm">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleBookDemo}
              className="hidden sm:flex border-purple-600 text-purple-600 hover:bg-purple-50 rounded-full"
            >
              Book Demo
            </Button>
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white rounded-full px-6">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Button 
                onClick={handleLogin} 
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white rounded-full px-6"
                data-testid="login-btn"
              >
                Get Started
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section - Purple Gradient */}
      <section className="pt-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 30%, #8B5CF6 60%, #A78BFA 100%)' }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                Powered by AI + Professional Studio
              </div>
              
              <p className="text-lg md:text-xl text-purple-100 mb-4">
                AI-Powered Product Photoshoot & Video Studio for Marketplaces
              </p>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Create. Shoot. <span className="text-pink-300">Scale.</span>
              </h1>
              
              <p className="text-lg text-purple-100 mb-8 max-w-lg">
                JiniStudio is Zaroori Retail's in-house AI + professional photoshoot studio, built exclusively for marketplace-ready content. We help brands create high-conversion images and videos that meet Myntra, Nykaa, AJIO, Tira, Tata CLiQ, and other premium platform guidelines.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleCall}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-full px-8 py-6 text-lg shadow-lg"
                >
                  <PhoneCall className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleWhatsApp}
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
            
            {/* Right - Logo/Image */}
            <div className="flex justify-center">
              <div className="bg-white/95 rounded-3xl p-8 shadow-2xl">
                <img src={LOGO_URL} alt="Jini Studio" className="w-64 md:w-80 h-auto" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave Decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* What is JiniStudio */}
      <section id="what-is" className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What is JiniStudio?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              JiniStudio is a subscription-based creative studio that provides unlimited fashion photoshoots, product videos, and AI-assisted content creation for brands selling on online marketplaces.
            </p>
          </div>
          
          {/* Benefits Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Package, title: "No per-shoot cost", desc: "All shoots under your subscription", color: "from-purple-500 to-purple-600" },
              { icon: Users, title: "No freelancer dependency", desc: "In-house expert team at your service", color: "from-purple-600 to-violet-600" },
              { icon: Clock, title: "No delays", desc: "Fast turnaround with priority scheduling", color: "from-violet-600 to-purple-700" }
            ].map((item, i) => (
              <Card key={i} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className={`p-6 bg-gradient-to-br ${item.color} text-white`}>
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-purple-100">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Marketplace Logos */}
          <div className="text-center">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">
              Compliant with all major marketplaces
            </p>
            <div className="flex justify-center items-center gap-8 flex-wrap">
              {[
                { name: "Myntra", color: "#ff3f6c" },
                { name: "Amazon", color: "#ff9900" },
                { name: "Flipkart", color: "#2874f0" },
                { name: "Ajio", color: "#000000" },
                { name: "Nykaa", color: "#fc2779" },
                { name: "Tata Cliq", color: "#e41e31" },
                { name: "Pernia Popup", color: "#c9a050" }
              ].map((logo, i) => (
                <div key={i} className="text-xl font-bold" style={{ color: logo.color }}>
                  {logo.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              JiniStudio Services - Fashion Category
            </h2>
            <p className="text-lg text-gray-600">
              From catalog images to model videos — everything under one roof.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: Camera, 
                title: "Professional Product Photoshoot", 
                color: "bg-pink-500",
                features: [
                  "Marketplace-compliant product images",
                  "White background & lifestyle shots",
                  "Flat-lay, mannequin & model-based shoots",
                  "Multi-angle coverage (front, back, close-ups)",
                  "Zoom & fabric detailing shots"
                ]
              },
              { 
                icon: Video, 
                title: "Model Videos with Products", 
                color: "bg-purple-500",
                features: [
                  "Short-form product videos for marketplaces",
                  "Model walk-through & movement videos",
                  "Fabric flow & fit demonstration",
                  "Reels-style vertical videos (for ads & PDPs)",
                  "Optimised for Myntra / Nykaa / AJIO guidelines"
                ]
              },
              { 
                icon: Wand2, 
                title: "AI-Powered Generation", 
                color: "bg-violet-500",
                features: [
                  "AI model photoshoots from flat-lay images",
                  "Background enhancement & replacement",
                  "Multi-view synthesis (front, back, side)",
                  "Automated texture & detail preservation",
                  "Rapid catalog generation at scale"
                ]
              }
            ].map((service, i) => (
              <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className={`w-14 h-14 ${service.color} rounded-2xl flex items-center justify-center mb-4`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <ul className="space-y-3">
                    {service.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-gray-600">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Comparison Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4">
              <Wand2 className="w-4 h-4" />
              AI-Powered Transformation
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See the Magic in Action
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Drag the slider to compare original product photos with AI-enhanced professional imagery
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {COMPARISON_IMAGES.map((item, index) => (
              <div key={index} className="group">
                <BeforeAfterSlider 
                  beforeImage={item.before}
                  afterImage={item.after}
                  beforeLabel="Original"
                  afterLabel="AI Enhanced"
                  className="shadow-xl group-hover:shadow-2xl transition-shadow duration-300"
                />
                <div className="mt-4 text-center">
                  <h3 className="font-bold text-gray-900 text-lg">{item.title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Button 
              onClick={handleLogin}
              className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white rounded-full px-8 py-6 text-lg"
            >
              Try It Now - Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Brands Choose - Purple Section */}
      <section id="why-choose" className="py-16 px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 50%, #8B5CF6 100%)' }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Brands Choose JiniStudio
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Built by marketplace experts, not generic photographers",
              "Content designed to sell, not just look good",
              "Unlimited usage under subscription",
              "Faster than freelancers or agencies",
              "Integrated with Zaroori Retail onboarding & account management",
              "Ideal for scaling catalogs & ads"
            ].map((benefit, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                <p className="text-white font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is it Best For */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who is JiniStudio Best For?
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: Star, title: "Fashion brands launching on marketplaces", color: "text-purple-500" },
              { icon: Shield, title: "Brands struggling with catalog approval", color: "text-violet-500" },
              { icon: Target, title: "Sellers running PLA / video ads", color: "text-pink-500" },
              { icon: Rocket, title: "D2C brands scaling SKUs rapidly", color: "text-indigo-500" },
              { icon: Award, title: "Brands wanting consistent creative quality", color: "text-purple-600" }
            ].map((item, i) => (
              <div key={i} className="text-center p-4">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-50 flex items-center justify-center`}>
                  <item.icon className={`w-8 h-8 ${item.color}`} />
                </div>
                <p className="text-sm font-medium text-gray-700">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Videos Section */}
      <section id="demo-videos" className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See JiniStudio in Action
            </h2>
            <p className="text-lg text-gray-600">
              Real AI-generated photoshoots and videos from our platform
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {DEMO_VIDEOS.map((video) => (
              <div key={video.id} className="group relative rounded-xl overflow-hidden bg-purple-100 aspect-[9/16] shadow-lg hover:shadow-xl transition-all">
                <video
                  src={video.url}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <span className="inline-block px-2 py-1 bg-white/20 backdrop-blur-sm rounded text-xs text-white mb-1">
                      {video.category}
                    </span>
                    <p className="text-white font-medium text-xs">{video.title}</p>
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center group-hover:opacity-0 transition-opacity">
                  <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                    <Play className="w-4 h-4 text-purple-600 ml-0.5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600">
              Choose the plan that fits your business
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { 
                name: "Starter", 
                price: "₹999", 
                features: ["50 SKU Credits", "200 AI Images", "50 Videos", "All Categories", "Email Support"],
                popular: false
              },
              { 
                name: "Pro", 
                price: "₹2,499", 
                features: ["200 SKU Credits", "800 AI Images", "200 Videos", "Priority Support", "Team Access"],
                popular: true
              },
              { 
                name: "Studio", 
                price: "₹4,999", 
                features: ["500 SKU Credits", "2000 AI Images", "500 Videos", "Dedicated Support", "API Access"],
                popular: false
              }
            ].map((plan, i) => (
              <Card key={i} className={`relative border-2 ${plan.popular ? 'border-purple-500 shadow-xl scale-105' : 'border-gray-200'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardContent className="pt-8 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-2 text-gray-600">
                        <Check className="w-5 h-5 text-purple-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={handleLogin}
                    className={`w-full ${plan.popular ? 'bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600' : 'bg-gray-900 hover:bg-gray-800'} text-white rounded-full`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6" style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 50%, #8B5CF6 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Product Photography?
          </h2>
          <p className="text-lg text-purple-100 mb-8">
            Join hundreds of brands using JiniStudio to create professional photoshoots at scale.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={handleLogin}
              className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-8 py-6 text-lg font-semibold"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline"
              onClick={handleBookDemo}
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
            >
              Book a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8 mb-8">
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="Jini Studio" className="h-12 w-auto mb-4 brightness-0 invert" />
              <p className="text-sm mb-4">
                AI-Powered Photoshoot Studio for Ecommerce Brands
              </p>
              <div className="text-sm space-y-2">
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  188/5, Kailash Sherpur Byepass, GT Road, Ludhiana, Punjab 141010
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  +91 7009008425
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Info@jinistudio.com
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#what-is" className="hover:text-white">What is JiniStudio</a></li>
                <li><a href="#services" className="hover:text-white">Services</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#demo-videos" className="hover:text-white">Demo Videos</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Site Map</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-white">Home</Link></li>
                <li><Link to="/dashboard" className="hover:text-white">Dashboard</Link></li>
                <li><Link to="/products" className="hover:text-white">Products</Link></li>
                <li><Link to="/shoots" className="hover:text-white">My Shoots</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/refund" className="hover:text-white">Refund Policy</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">© 2026 JiniStudio. All rights reserved.</p>
            <a href="https://zarooriretail.com" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white">
              Made by Zaroori Infotech
            </a>
          </div>
        </div>
      </footer>

      {/* Floating CTA Buttons */}
      <div className="fixed bottom-6 left-6 z-50">
        <Button 
          onClick={handleBookDemo}
          className="bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white rounded-full px-6 py-3 shadow-lg"
        >
          <Video className="w-5 h-5 mr-2" />
          Book a Demo
          <span className="block text-xs opacity-80">Free 25-min session</span>
        </Button>
      </div>
      
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        <Button 
          onClick={handleWhatsApp}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-14 h-14 p-0 shadow-lg"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
        <Button 
          onClick={handleCall}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full w-14 h-14 p-0 shadow-lg"
        >
          <PhoneCall className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
}
